import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { gap } from './landingContent';
import { C, FONT, MONO, eyebrowSx, h2Sx, sectionSx, wrapSx, zoomVh } from './landingTokens';
import { HEADER_HEIGHT, clamp01, useScrollProgress } from './scrollStory';

/**
 * "The gap": a straight grey plan line and a red road line that steps further
 * away from it at four points, one per gap, with the four gaps as columns below.
 *
 * On desktop the section pins while you scroll through it: the plan line draws,
 * then the road line draws along it, and as the road reaches each point its
 * marker, label and column appear. Phones and reduced motion get the finished
 * drawing, unpinned.
 */

// Drawing, in viewBox units (1104 x 214). The plan runs straight along PLAN_Y;
// the road steps down at each TICK to the next of ROAD_Y.
const VB_W = 1104;
const VB_H = 214;
const PLAN_Y = 40;
const TICKS = [150, 432, 714, 996];
const ROAD_Y = [40, 66, 98, 136, 182];
const ROAD_D =
  'M0 40 L150 40 C185 40 195 66 230 66 L432 66 C467 66 477 98 512 98 L714 98 ' +
  'C749 98 759 136 794 136 L996 136 C1031 136 1041 182 1076 182 L1084 182';
// How far along the road path (0..1) each tick sits, from the segment lengths.
const TICK_AT = [0.134, 0.39, 0.648, 0.908];

const RED = '#c2332b';

// Scroll timeline: the plan draws over the first 15%, the road from 12% to 90%.
const planDrawn = (p: number) => clamp01(p / 0.15);
const roadDrawn = (p: number) => clamp01((p - 0.12) / 0.78);
// Each point (and its column) fades in over a short stretch once the road gets there.
const pointShown = (road: number, i: number) => clamp01((road - TICK_AT[i]) / 0.07);

const GapDrawing: React.FC<{ progress: number; compact: boolean }> = ({ progress, compact }) => {
  const plan = planDrawn(progress);
  const road = roadDrawn(progress);
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      role="img"
      aria-label="A straight plan line, and the road line drifting further away from it at four points: generic rules, bad data, lost know-how and no visibility."
      style={{ display: 'block', overflow: 'visible' }}
    >
      <line
        x1={0}
        y1={PLAN_Y}
        x2={1060}
        y2={PLAN_Y}
        pathLength={1}
        strokeDasharray="1 1"
        strokeDashoffset={1 - plan}
        stroke="#c9cfda"
        strokeWidth={2}
      />
      <path d={ROAD_D} pathLength={1} strokeDasharray="1 1" strokeDashoffset={1 - road} fill="none" stroke={RED} strokeWidth={2} />

      {TICKS.map((x, i) => {
        const shown = pointShown(road, i);
        return (
          <g key={x} style={{ opacity: shown }}>
            <line x1={x} y1={PLAN_Y} x2={x} y2={VB_H} stroke={RED} strokeWidth={1} strokeDasharray="2 3" />
            <circle cx={x} cy={ROAD_Y[i]} r={5} fill="#ffffff" stroke={RED} strokeWidth={2} />
            {!compact && (
              <text x={x + 10} y={ROAD_Y[i] - 12} fontFamily={MONO} fontSize={11} fill={RED}>
                {gap.items[i].mark}
              </text>
            )}
          </g>
        );
      })}

      {!compact && (
        <g fontFamily={MONO} fontSize={11} letterSpacing={1}>
          <text x={1068} y={PLAN_Y + 4} fill={C.subtle} style={{ opacity: plan }}>
            PLAN
          </text>
          <text x={1076} y={206} fill={RED} textAnchor="end" style={{ opacity: clamp01((road - 0.95) / 0.05) }}>
            ROAD
          </text>
        </g>
      )}
    </svg>
  );
};

const GapSection: React.FC = () => {
  const wide = useMediaQuery('(min-width: 900px)');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const animate = wide && !reduceMotion;
  const sectionRef = useRef<HTMLElement | null>(null);
  const progress = useScrollProgress(sectionRef, animate);
  const road = roadDrawn(progress);

  const content = (
    <Box sx={wrapSx}>
      <Typography sx={eyebrowSx}>The gap</Typography>
      <Typography component="h2" sx={{ ...h2Sx, mt: 1.5 }}>
        {gap.title}
      </Typography>

      <Box sx={{ mt: { xs: 4, md: 6.5 } }}>
        <GapDrawing progress={progress} compact={!wide} />
      </Box>

      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          mt: { xs: 3, md: 1 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 3.5,
        }}
      >
        {gap.items.map((item, i) => {
          const shown = pointShown(road, i);
          return (
            <Box
              component="li"
              key={item.title}
              sx={{ opacity: shown, transform: `translateY(${(1 - shown) * 14}px)` }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', fontFamily: MONO, fontSize: 12 }}>
                <Box component="span" sx={{ color: C.subtle }}>
                  {String(i + 1).padStart(2, '0')}
                </Box>
                {!wide && (
                  <Box component="span" sx={{ color: RED }}>
                    {item.mark}
                  </Box>
                )}
              </Box>
              <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 17, lineHeight: 1.3, letterSpacing: '-0.015em', color: C.ink, mt: 1 }}>
                {item.title}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.6, color: C.body, mt: 1 }}>{item.description}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (!animate) {
    return (
      <Box component="section" id="gap" sx={sectionSx}>
        {content}
      </Box>
    );
  }

  return (
    <Box component="section" id="gap" ref={sectionRef} sx={{ position: 'relative', height: zoomVh(240), scrollMarginTop: `${HEADER_HEIGHT}px` }}>
      <Box sx={{ position: 'sticky', top: HEADER_HEIGHT, height: zoomVh(100, HEADER_HEIGHT), display: 'flex', alignItems: 'center' }}>
        {content}
      </Box>
    </Box>
  );
};

export default GapSection;
